import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { motion } from 'framer-motion';
import { Phone, Mail, Clock, Check, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  subject: z.string().min(5, 'Subject must be at least 5 characters'),
  message: z.string().min(20, 'Message must be at least 20 characters'),
});

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      await axios.post(`${API}/contact`, data);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitError('Failed to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full text-orange-500 font-body text-sm font-medium mb-4"
          >
            Get In Touch
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase"
          >
            Contact <span className="text-orange-500">Us</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-base lg:text-lg text-slate-400 font-body max-w-2xl mx-auto"
          >
            Have questions? We&apos;re here to help. Reach out and our team will respond promptly.
          </motion.p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-navy-900 mb-6">
                  Contact Information
                </h2>
                <p className="font-body text-slate-600 mb-8">
                  Ready to move your freight? Have questions about our services? We&apos;re here to help.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-body font-semibold text-navy-900">Phone</h3>
                    <a
                      href="tel:+18888984591"
                      className="font-body text-slate-600 hover:text-orange-500 transition-colors duration-200"
                    >
                      (888) 898-4591
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-body font-semibold text-navy-900">Email</h3>
                    <div className="space-y-1">
                      <a
                        href="mailto:info@summitmoves.com"
                        className="block font-body text-slate-600 hover:text-orange-500 transition-colors duration-200"
                      >
                        info@summitmoves.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-body font-semibold text-navy-900">Hours</h3>
                    <p className="font-body text-slate-600">Monday - Friday: 7am - 7pm CST</p>
                    <p className="font-body text-slate-600">Saturday: 8am - 2pm CST</p>
                    <p className="font-body text-sm text-orange-600 mt-1">24/7 Emergency Support Available</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-50 rounded-2xl p-8 lg:p-10">
                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                    data-testid="contact-success-message"
                  >
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-navy-900 mb-4">
                      Message Sent!
                    </h3>
                    <p className="font-body text-slate-600">
                      Thank you for reaching out. We&apos;ll get back to you shortly.
                    </p>
                  </motion.div>
                ) : (
                  <>
                    <h3 className="font-heading text-2xl font-bold text-navy-900 mb-6">
                      Send Us a Message
                    </h3>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="name" className="font-body font-medium text-navy-900">
                            Full Name *
                          </Label>
                          <Input
                            id="name"
                            {...register('name')}
                            placeholder="John Smith"
                            className="mt-2"
                            data-testid="contact-input-name"
                          />
                          {errors.name && (
                            <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="email" className="font-body font-medium text-navy-900">
                            Email Address *
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            {...register('email')}
                            placeholder="john@company.com"
                            className="mt-2"
                            data-testid="contact-input-email"
                          />
                          {errors.email && (
                            <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                          )}
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="phone" className="font-body font-medium text-navy-900">
                            Phone Number
                          </Label>
                          <Input
                            id="phone"
                            type="tel"
                            {...register('phone')}
                            placeholder="(555) 123-4567"
                            className="mt-2"
                            data-testid="contact-input-phone"
                          />
                        </div>

                        <div>
                          <Label htmlFor="subject" className="font-body font-medium text-navy-900">
                            Subject *
                          </Label>
                          <Input
                            id="subject"
                            {...register('subject')}
                            placeholder="How can we help?"
                            className="mt-2"
                            data-testid="contact-input-subject"
                          />
                          {errors.subject && (
                            <p className="mt-1 text-sm text-red-500">{errors.subject.message}</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="message" className="font-body font-medium text-navy-900">
                          Message *
                        </Label>
                        <Textarea
                          id="message"
                          {...register('message')}
                          placeholder="Tell us about your inquiry..."
                          className="mt-2 min-h-[150px]"
                          data-testid="contact-input-message"
                        />
                        {errors.message && (
                          <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>
                        )}
                      </div>

                      {submitError && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <p className="font-body text-sm text-red-600">{submitError}</p>
                        </div>
                      )}

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-body font-semibold py-6"
                        data-testid="contact-submit-btn"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Send Message'
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
